"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type School = [name: string, town: string, outwardCode: string];

const MAX_RESULTS = 7;

// 350KB of school data, so it's fetched once on first focus rather than
// shipped in the page bundle, and shared across every mount after that.
let cache: School[] | null = null;
let inFlight: Promise<School[]> | null = null;

function loadSchools(): Promise<School[]> {
  if (cache) return Promise.resolve(cache);
  inFlight ??= fetch("/data/uk-schools.json")
    .then((r) => (r.ok ? (r.json() as Promise<School[]>) : []))
    .then((list) => {
      cache = list;
      return list;
    })
    .catch(() => []);
  return inFlight;
}

const normalise = (s: string) =>
  s
    .toLowerCase()
    .replace(/[’']/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * Free-text school field with suggestions from the four nations' registers
 * (see scripts/build-school-list.ts). Typing wins over the list, so a school
 * that has just opened or been renamed can still be submitted.
 */
export function SchoolCombobox({
  id,
  value,
  onChange,
  className,
  placeholder,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
}) {
  const [schools, setSchools] = useState<School[]>([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [picked, setPicked] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open || schools.length) return;
    let alive = true;
    loadSchools().then((list) => alive && setSchools(list));
    return () => {
      alive = false;
    };
  }, [open, schools.length]);

  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  const matches = useMemo(() => {
    const q = normalise(value);
    if (q.length < 2 || picked) return [];
    const starts: School[] = [];
    const contains: School[] = [];
    for (const school of schools) {
      const name = normalise(school[0]);
      if (name.startsWith(q)) starts.push(school);
      else if (name.includes(q)) contains.push(school);
      if (starts.length >= MAX_RESULTS) break;
    }
    return [...starts, ...contains].slice(0, MAX_RESULTS);
  }, [schools, value, picked]);

  const visible = open && matches.length > 0;

  const choose = (school: School) => {
    onChange(school[0]);
    setPicked(true);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <input
        id={id}
        autoComplete="off"
        role="combobox"
        aria-expanded={visible}
        aria-autocomplete="list"
        className={className}
        value={value}
        placeholder={placeholder}
        onFocus={() => setOpen(true)}
        onChange={(e) => {
          onChange(e.target.value);
          setPicked(false);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={(e) => {
          if (!visible) return;
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setActive((i) => (i + 1) % matches.length);
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActive((i) => (i - 1 + matches.length) % matches.length);
          } else if (e.key === "Enter") {
            e.preventDefault();
            const match = matches[active];
            if (match) choose(match);
          } else if (e.key === "Escape") {
            setOpen(false);
          }
        }}
      />

      {visible ? (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-2xl border border-border bg-background py-1 shadow-lg"
        >
          {matches.map((school, i) => (
            <li key={`${school[0]}-${school[2]}-${i}`}>
              <button
                type="button"
                role="option"
                aria-selected={i === active}
                onMouseEnter={() => setActive(i)}
                onClick={() => choose(school)}
                className={cn(
                  "flex w-full items-baseline justify-between gap-4 px-4 py-2.5 text-left transition-colors",
                  i === active ? "bg-primary/5" : "",
                )}
              >
                <span className="truncate font-polysans text-sm">{school[0]}</span>
                <span className="shrink-0 font-polysans text-xs font-light text-muted-foreground">
                  {[school[1], school[2]].filter(Boolean).join(", ")}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
